import { LogicNameFilter, Platforms } from "../../../../../utils/constants";
import { ALL_OPTION_NAME, filterName, pastTimeOption } from "../utils";
import {
  areAllFiltersNotApplied,
  createEmptyFilters,
  EDRTools,
  isMissingEDRApplied,
  isMissingMDMApplied,
  MDMTools,
} from "./utils";

// NOTE - in October 2025 Ventura (13) will be deprecated
export const SUPPORTED_MAC_OS_VERSION_NAMES = [
  "Ventura",
  "Sonoma",
  "Sequoia",
  "Tahoe",
];
export const MIN_SUPPORTED_MAC_OS_VERSION = 13;

// NOTE - in October 2025 3 versions in the list will be deprecated
export const SUPPORTED_WINDOWS_BASE_BUILD_NUMBERS = [
  "10.0.26100",
  "10.0.22631",
  "10.0.22621",
  "10.0.19045",
  "10.0.19044",
  "10.0.17763",
  "10.0.14393",
  "10.0.10240",
];

export const getHostSmartFiltersInfo = (
  fetchedEntities,
  appliedFilters,
  setAppliedFilters,
  filterAndOrLogics,
  setFilterAndOrLogics
) => {
  const inactive90DaysApplied =
    appliedFilters[filterName.HOST_LAST_SEEN]?.[pastTimeOption.LAST_90_DAYS] &&
    filterAndOrLogics[filterName.HOST_LAST_SEEN] ===
      LogicNameFilter.TIME_BEFORE;

  const existingVersionsByOS = fetchedEntities.reduce(
    (acc, { platform, os_version }) => {
      if (!os_version) return acc;
      if (!acc[platform]) {
        acc[platform] = [];
      }
      if (!acc[platform].includes(os_version)) acc[platform].push(os_version);
      return acc;
    },
    {}
  );

  const isWindowsVersionSupported = (version) => {
    // No information = assuming it's supported
    if (!version) return true;
    if (
      SUPPORTED_WINDOWS_BASE_BUILD_NUMBERS.some((baseBuildNumber) =>
        version.includes(baseBuildNumber.toLowerCase())
      )
    )
      return true;
    // Windows server should not appear as endpoints
    if (version.toLowerCase().includes("server")) return true;
    // single case assuming it's supported since only one Windows 11 version is deprecated
    if (version === "Windows 11") return true;
    return false;
  };

  const isMacOSVersionSupported = (version) => {
    // No information = assuming it's supported
    if (!version) return true;
    // check by version name
    if (
      SUPPORTED_MAC_OS_VERSION_NAMES.some((versionName) =>
        version.toLowerCase().includes(versionName.toLowerCase())
      )
    )
      return true;
    // check by version major
    if (!isNaN(version.charAt(0))) {
      const major = version.split(".")[0];
      if (Number(major) >= MIN_SUPPORTED_MAC_OS_VERSION) return true;
    }
    return false;
  };

  const anyDeprecatedOSVersion = Object.entries(existingVersionsByOS).some(
    ([os, versions]) => {
      if (os === Platforms.WINDOWS) {
        return versions.some((version) => !isWindowsVersionSupported(version));
      }
      if (os === Platforms.MACOS) {
        return versions.some((version) => !isMacOSVersionSupported(version));
      }
      return false;
    }
  );

  const isDeprecatedOSApplied = () => {
    if (
      !Object.keys(appliedFilters).includes(filterName.HOST_OS_FULL_VERSION) ||
      Object.keys(appliedFilters[filterName.HOST_OS_FULL_VERSION]).length === 0
    )
      return false;

    const allWindowsDeprecatedVersionsChecked = (
      existingVersionsByOS[Platforms.WINDOWS] || []
    )
      .filter((version) => !isWindowsVersionSupported(version))
      .every(
        (version) => appliedFilters[filterName.HOST_OS_FULL_VERSION][version]
      );

    const allMacDeprecatedVersionsChecked = (
      existingVersionsByOS[Platforms.MACOS] || []
    )
      .filter((version) => !isMacOSVersionSupported(version))
      .every(
        (version) => appliedFilters[filterName.HOST_OS_FULL_VERSION][version]
      );

    if (
      !allMacDeprecatedVersionsChecked ||
      !allWindowsDeprecatedVersionsChecked
    )
      return false;

    return Object.entries(
      appliedFilters[filterName.HOST_OS_FULL_VERSION]
    ).every(([version, checked]) => {
      if (version === ALL_OPTION_NAME) return true;
      if (
        existingVersionsByOS[Platforms.WINDOWS]?.includes(version) &&
        isWindowsVersionSupported(version)
      )
        return !checked;
      if (
        existingVersionsByOS[Platforms.MACOS]?.includes(version) &&
        isMacOSVersionSupported(version)
      )
        return !checked;
      return true;
    });
  };

  return [
    {
      text: "Endpoint is inactive for 90 days",
      applied:
        inactive90DaysApplied &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.HOST_LAST_SEEN],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);
        updatedFilters[filterName.HOST_LAST_SEEN][
          pastTimeOption.LAST_90_DAYS
        ] = true;
        setAppliedFilters(updatedFilters);
        setFilterAndOrLogics((prev) => ({
          ...prev,
          [filterName.HOST_LAST_SEEN]: LogicNameFilter.TIME_BEFORE,
        }));
      },
    },
    {
      text: "Endpoint missing EDR",
      applied:
        isMissingEDRApplied(fetchedEntities, appliedFilters) &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.EXCLUDE_SOURCE],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);
        updatedFilters[filterName.EXCLUDE_SOURCE] = Object.fromEntries(
          EDRTools.map((tool) => [tool, true])
        );
        setAppliedFilters(updatedFilters);
      },
    },
    {
      text: "Endpoint missing MDM",
      applied:
        isMissingMDMApplied(fetchedEntities, appliedFilters) &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.EXCLUDE_SOURCE],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);
        updatedFilters[filterName.EXCLUDE_SOURCE] = Object.fromEntries(
          MDMTools.map((tool) => [tool, true])
        );
        setAppliedFilters(updatedFilters);
      },
    },
    {
      text: "Deprecated OS",
      applied:
        isDeprecatedOSApplied() &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.HOST_OS_FULL_VERSION],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);
        Object.entries(existingVersionsByOS).forEach(([os, versions]) => {
          if (os === Platforms.WINDOWS) {
            versions.forEach((version) => {
              if (!isWindowsVersionSupported(version))
                updatedFilters[filterName.HOST_OS_FULL_VERSION][version] = true;
              else
                delete updatedFilters[filterName.HOST_OS_FULL_VERSION][version];
            });
          } else if (os === Platforms.MACOS) {
            versions.forEach((version) => {
              if (!isMacOSVersionSupported(version))
                updatedFilters[filterName.HOST_OS_FULL_VERSION][version] = true;
              else
                delete updatedFilters[filterName.HOST_OS_FULL_VERSION][version];
            });
          } else
            versions.forEach((version) => {
              delete updatedFilters[filterName.HOST_OS_FULL_VERSION][version];
            });
        });
        setAppliedFilters(updatedFilters);
      },
      disabled: !anyDeprecatedOSVersion,
    },
  ];
};
